import { useEffect, useState } from "react";
import Help from "../components/Help";
import PageHeader from "../components/PageHeader";
import infoCircle from "../assets/info-circle.svg";
import LogoUploader from "../components/LogoUploader";
import TagInput from "../components/TagInput";
import { helpPagesData } from "../utils/helper";
import { fetchSettings, UpdateSettings } from "../action/settingaction";
import { useToast } from "../components/ToastProvider";
import { requestWithToken } from "../utils/apis";
import MediaPickerModal from "../components/MediaPickerModal";

export default function Settings() {
  const { showToast } = useToast();
  const [errors, setErrors] = useState({});
  const [settings, setSettings] = useState(null);
  const [tags, setTags] = useState([]);
  const [mediaModal, setMediaModal] = useState({ open: false, field: "" });

  // Fetch data when page loads
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    const res = await fetchSettings();
    if (res?.status === 1) {
      setSettings(res.data);

      // Convert string to array for TagInput
      const focusStr = res.data?.seo?.focus_keywords || "";
      const focusArr = focusStr
        ? focusStr.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      setTags(focusArr);
    }
  };

  const handleFileUpload = async (file, fieldName) => {
    const formData = new FormData();
    formData.append("file", file);

    const uploadRes = await requestWithToken("POST", "/api/upload", formData);
    if (uploadRes?.status === 1) {
      const filePath = uploadRes.data.path; // e.g. media/xxxx.png
      setSettings((prev) => ({ ...prev, [fieldName]: filePath }));
      showToast("Image uploaded", "success");
    } else {
      showToast("Upload failed", "error");
    }
  };

  // Update inputs
  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSeoChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      seo: { ...prev.seo, [field]: value },
    }));
  };

  const handleGoogleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      google_site: { ...prev.google_site, [field]: value },
    }));
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!settings.title?.trim()) newErrors.title = true;
    if (!settings.tagline?.trim()) newErrors.tagline = true;
    if (!settings.site_logo) newErrors.site_logo = true;
    if (!settings.fevicon) newErrors.fevicon = true;

    // if (!tags.length) newErrors.focus_keywords = true;
    // if (!settings?.seo?.tagline?.trim()) newErrors.seo_tagline = true;
    // if (!settings?.seo?.desc?.trim()) newErrors.seo_desc = true;

    // If errors exist → stop save
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast("Please fill required fields", "error");
      return;
    }

    setErrors({}); // clear previous errors

    const payload = {
      title: settings.title,
      tagline: settings.tagline,
      fevicon: settings.fevicon,
      site_logo: settings.site_logo,

      code_snippet: {
        head_tag: settings?.code_snippet?.head_tag,
      },

      google_site: {
        measurement_id: settings?.google_site?.measurement_id,
        script: settings?.google_site?.script,
      },

      seo: {
        // send back as comma-separated string
        focus_keywords: tags.join(","),
        tagline: settings?.seo?.tagline,
        desc: settings?.seo?.desc,
      },
    };

    const res = await UpdateSettings(payload);

    if (res?.status === 1) {
      showToast("Settings updated successfully", "success");
      loadSettings();
    } else {
      showToast(res?.message || "Failed to update settings", "error");
    }
  };

  // if (!settings) return null;

  return (
    <>
      <PageHeader pageTitle="Settings">
          <Help
            title={helpPagesData.pages.title}
            message={helpPagesData.pages.message}
            items={helpPagesData.pages.items}
          />
          <button className="btn-black" onClick={handleSubmit}>
            Save Changes
          </button>
      </PageHeader>

      <div className="page-container">
        <div className="row">
          <div className="col-lg-8">

            {/* GENERAL */}
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">General Details</div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Site Title
                  <img className="ms-2" src={infoCircle} alt="Info" title="enter site title" />
                </label>
                <input
                  type="text"
                  className="frm-input"
                  value={settings?.title || ""}
                  placeholder="Enter site title"
                  onChange={(e) => handleChange("title", e.target.value)}
                />
                {errors.title && <div className="input-error">Fill this</div>}
              </div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">Tagline
                  <img className="ms-2" src={infoCircle} alt="Info" title="enter Tagline" />
                </label>
                <input
                  className="frm-input"
                  value={settings?.tagline || ""}
                  placeholder="Enter site tagline"
                  onChange={(e) => handleChange("tagline", e.target.value)}
                />
                {errors.tagline && <div className="input-error">Fill this</div>}
              </div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">Site Logo
                  <img className="ms-2" src={infoCircle} alt="Info" title="enter Site Logo" />
                </label>
                <LogoUploader
                  defaultLogo={settings?.site_logo}
                  onSelect={() => setMediaModal({ open: true, field: "site_logo" })}
                />

                {errors.site_logo && <div className="input-error">Upload logo</div>}
              </div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">Favicon
                  <img className="ms-2" src={infoCircle} alt="Info" title="enter Favicon" />
                </label>

                <LogoUploader
                  defaultLogo={settings?.fevicon}
                  onSelect={() => setMediaModal({ open: true, field: "fevicon" })}
                />

                {errors.fevicon && <div className="input-error">Upload favicon</div>}
              </div>
            </div>

            {/* GLOBAL CODE */}
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">Global Code Snippets</div>
              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Head Tag
                  <img
                    className="ms-2"
                    src={infoCircle}
                    alt="Info"
                    title="Add Head Tag"
                  />
                </label>
                <textarea
                  className="frm-input"
                  rows={3}
                  value={settings?.code_snippet?.head_tag || ""}
                  placeholder="Paste global head tag script here"
                  onChange={(e) =>
                    handleChange("code_snippet", { head_tag: e.target.value })
                  }
                />
               
              </div>
            </div>

            {/* SEO */}
            <div className="theme-card p-4 mb-3">
              <div className="card-title pb-3">SEO</div>
              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Focus Keywords
                  <img
                    className="ms-2"
                    src={infoCircle}
                    alt="Info"
                    title="Add keywords of Page eg. About Us and press enter to add"
                  />
                </label>
                <div className="tag-card">
                  {/* ✅ Use TagInput correctly with value/onChange */}
                  <TagInput
                    value={tags}
                    onChange={setTags}
                  />
                </div>
              </div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Tagline
                  <img
                    className="ms-2"
                    src={infoCircle}
                    alt="Info"
                    title="Add title of Page eg. About Us"
                  />
                </label>
                <input
                  className="frm-input mt-3"
                  value={settings?.seo?.tagline || ""}
                  placeholder="Enter SEO tagline"
                  onChange={(e) => handleSeoChange("tagline", e.target.value)}
                />
                {errors.seo_tagline && <div className="input-error">Fill this</div>}
              </div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Meta Description
                  <img
                    className="ms-2"
                    src={infoCircle}
                    alt="Info"
                    title="Add Meta Description"
                  />
                </label>
                <textarea
                  className="frm-input mt-3"
                  rows={3}
                  value={settings?.seo?.desc || ""}
                  placeholder="Enter meta description"
                  onChange={(e) => handleSeoChange("desc", e.target.value)}
                />
                {errors.seo_desc && <div className="input-error">Fill this</div>}
              </div>
            </div>

            {/* GOOGLE SITE KIT */}
            <div className="theme-card p-4 mb-4">
              <div className="card-title pb-3">Google Site Kit Integration</div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Measurement ID
                  <img
                    className="ms-2"
                    src={infoCircle}
                    alt="Info"
                    title="Add Measurement ID"
                  />
                </label>
                <input
                  className="frm-input"
                  value={settings?.google_site?.measurement_id || ""}
                  placeholder="Enter GA Measurement ID"
                  onChange={(e) =>
                    handleGoogleChange("measurement_id", e.target.value)
                  }
                />
               
              </div>

              <div className="frm-group">
                <label className="frm-label d-flex align-items-center">
                  Script
                  <img
                    className="ms-2"
                    src={infoCircle}
                    alt="Info"
                    title="Add Script"
                  />
                </label>
                <textarea
                  className="frm-input mt-3"
                  rows={3}
                  value={settings?.google_site?.script || ""}
                  placeholder="Paste Google script here"
                  onChange={(e) => handleGoogleChange("script", e.target.value)}
                />
              
              </div>
            </div>

          </div>
        </div>
      </div>
      {mediaModal.open && (
        <MediaPickerModal
          show={mediaModal.open}
          onClose={() => setMediaModal({ open: false, field: "" })}
          onSelect={(file) => {
            setSettings((prev) => ({
              ...prev,
              [mediaModal.field]: file.startsWith("http")
                ? file.replace(process.env.REACT_APP_BUCKET_URL, "") // store path only
                : file
            }));
            setMediaModal({ open: false, field: "" });
            showToast("Image selected", "success");
          }}

        />
      )}

    </>
  );
}
